import { Injectable, inject } from '@angular/core';
import { Crop, getCrops } from '@next/arable-lib/crop';
import { GeoBrowserService, MapColors, InteractionService } from '@next/arable-lib/geo';
import { select, Store } from '@ngrx/store';
import { GeoJSON } from 'ol/format';
import VectorLayer from 'ol/layer/Vector';
import { Vector } from 'ol/source';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { combineLatest, ReplaySubject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { SiteState, SyncStatus } from 'src/app/shared/models';

import { selectBlightData, selectCustomerBlightRights } from 'src/app/store/selectors';

import { BlightData, Day, PotatoAgrifirmCropIds } from '../../../../blight-v2/models/models';

import { DateService } from '../../../../shared/services/date.service';
import { Feature } from '../map.models';
import { setBlightLayerEnabled } from '../store/map.actions';
import { getBlightLayerEnabled, getFeatures } from '../store/map.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectBlightService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dateService = inject(DateService);

  crops: Crop[] = [];

  readonly source = new Vector({
    wrapX: false,
  });

  readonly layer = new VectorLayer({
    source: this.source,
    style: this.getStyle,
    zIndex: 2,
  });

  private reader = new GeoJSON({});
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  init(geoService: GeoBrowserService): void {
    geoService.map.addLayer(this.layer);
    this.getCrops();

    combineLatest([
      this.store.select(selectBlightData),
      this.store.select(selectCustomerBlightRights),
      this.store.select(getFeatures),
    ])
      .pipe(takeUntil(this.destroyed$), debounceTime(1000))
      .subscribe(([blightData, customerBlightRights, features]) => {
        if (customerBlightRights.isPremium && blightData?.length && blightData[0].syncStatus !== SyncStatus.mock) {
          this.source.clear();
          this.enableBlightWarningsLayer();
          this.updateFeatures(features, blightData);
        }
      });

    this.store.pipe(select(getBlightLayerEnabled), takeUntil(this.destroyed$))
      .subscribe((enabled) => this.layer.setVisible(enabled));
  }

  start(_: GeoBrowserService): void { }

  stop(_: GeoBrowserService): void { }

  getStyle() {
    return new Style({
      stroke: new Stroke({
        color: MapColors.blightPreventiveStroke,
        width: 2,
        lineDash: [1, 4],
        lineCap: 'round',
      }),
      fill: new Fill({
        color: MapColors.blightPreventiveFill,
      }),
    });
  }

  destroy(geoService: GeoBrowserService) {
    this.layer.dispose();
    geoService.map.removeLayer(this.layer);
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private updateFeatures(features: Feature[], blightData: BlightData[]) {
    const blightDataMap = new Map(
      blightData.map((item) => [item.cropFieldId, item]),
    );

    const potatoCropIdsSet = new Set(
      this.crops
        .filter((crop) => crop.agrifirmCrop?.id && PotatoAgrifirmCropIds.includes(crop.agrifirmCrop.id))
        .map((crop) => crop.id),
    );

    features.forEach((feature) => {
      const featureId = +feature.id;
      const blightItem = blightDataMap.get(featureId);

      if (!blightItem?.isCropFieldVisible) return;

      if (!potatoCropIdsSet.has(feature.cropId)) return;

      const blightDataForToday = this.getBlightDataForToday([blightItem], featureId);
      if (blightDataForToday?.sprayType !== undefined && blightDataForToday?.sprayType > 0) {
        const olFeature = this.reader.readFeature(feature);
        olFeature.setId(feature.id);
        this.source.addFeature(olFeature);
      }
    });
  }

  private getCrops() {
    this.store.pipe(select(getCrops), takeUntil(this.destroyed$)).subscribe((crops) => {
      this.crops = crops;
    });
  }

  private enableBlightWarningsLayer() {
    this.store.dispatch(setBlightLayerEnabled(true));
  }

  private getBlightDataForToday(blightData: BlightData[], cropFieldId: number): Day | undefined {
    const blightDaysResult = blightData?.find((x) => x.cropFieldId === cropFieldId)?.blightResult?.days;
    const blightWithSprayAdvice = blightDaysResult?.filter((x) => x.sprayType > 0);
    return blightWithSprayAdvice?.find((x) => this.dateService.isDateToday(new Date(x.date)));
  }
}
